import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../services/api';
import { RefreshCw, Loader2, AlertTriangle, Play, Save, RotateCcw, ChevronDown, ChevronRight } from 'lucide-react';

// Tooltip component with 0.5s delay
function Tip({ text, children }: { text: string; children: React.ReactNode }) {
  const [show, setShow] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => { timer.current = setTimeout(() => setShow(true), 500); }}
      onMouseLeave={() => { clearTimeout(timer.current); setShow(false); }}
    >
      {children}
      {show && (
        <span className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-800 rounded-lg shadow-lg whitespace-normal min-w-[280px] max-w-[420px] text-left pointer-events-none leading-relaxed">
          {text}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
        </span>
      )}
    </span>
  );
}

// Tooltip descriptions for every constant
const CONSTANT_TIPS: Record<string, string> = {
  // Page Geometry
  page_width_in: 'Physical page width in inches (US Letter = 8.5"). Defines the CSS max-width constraint.',
  page_height_in: 'Physical page height in inches (US Letter = 11"). Combined with scale to determine usable vertical space per page.',
  chromium_scale: 'Chromium Page.printToPDF scale factor (0.8 = 80%). Effectively enlarges the CSS viewport: a 0.8 scale means 11" page has 13.75" of CSS layout space.',
  body_padding_top_in: 'Top padding inside the body element in print media (CSS: @media print body padding-top).',
  body_padding_bottom_in: 'Bottom padding inside the body element in print media.',
  body_padding_left_in: 'Left padding inside the body element in print media. Reduces available content width.',
  body_padding_right_in: 'Right padding inside the body element in print media. Reduces available content width.',
  body_max_width_in: 'CSS max-width of the body element. Content will not exceed this width regardless of viewport size.',

  // Font Sizes
  font_size_name_pt: 'Font size of the candidate name (h1) in the header. CSS var: --font-size-name (22pt).',
  font_size_base_pt: 'Base font size for body text, summary, and role titles. CSS var: --font-size-base (10.5pt).',
  font_size_section_pt: 'Font size for section headings (h2). CSS var: --font-size-section (11.5pt).',
  font_size_sm_pt: 'Small font size used for experience bullets, skills items, company meta, and minor item titles. CSS var: --font-size-sm (9.5pt).',
  font_size_xs_pt: 'Extra-small font size used for minor item detail lines (org, year). CSS var: --font-size-xs (8.5pt).',
  font_size_contact_pt: 'Font size for contact items in the header (7.5pt). Smaller to fit multiple items in the 25% width column.',

  // Line Heights
  line_height: 'Default CSS line-height multiplier (1.45). Applied to all text except contacts. Multiplied by font size to get vertical space per line.',
  contact_line_height: 'Line-height for contact items (1.0). Tighter spacing since contact items are single-line with icon alignment.',

  // Spacing
  spacing_xs_in: 'Extra-small spacing in inches (~0.15rem). Used for highlight bullet bottom margins, section title padding.',
  spacing_sm_in: 'Small spacing in inches (~0.35rem). Used for minor item margins, company description bottom margin.',
  spacing_md_in: 'Medium spacing in inches (~0.65rem). Used for header padding, section title margin, company/role bottom margins.',
  spacing_lg_in: 'Large spacing in inches (~1.0rem). Used for section bottom margins, header bottom margin.',
  spacing_xl_in: 'Extra-large spacing in inches (~1.4rem). Used for column gaps in skills and minor grids.',

  // Header
  header_name_margin_top_em: 'Margin above the name (h1) in em units. Centers the name vertically within the header block.',
  header_padding_bottom_in: 'Padding below the header content, before the bottom border line.',
  header_border_in: 'Height of the header bottom border (2.5px solid line separating header from content).',
  header_margin_bottom_in: 'Space between header border and the first section below it.',
  header_contact_width_pct: 'Fraction of content width allocated to contact column (0.25 = 25%). Name takes the remaining 75%.',

  // Section Title
  section_title_padding_bottom_in: 'Padding below section title text, before the underline border.',
  section_title_border_in: 'Height of the section title underline (1.5px solid border).',
  section_title_margin_bottom_in: 'Space between section title underline and the section content below.',
  section_margin_bottom_in: 'Space after each entire section before the next section begins.',

  // Experience
  exp_company_margin_bottom_in: 'Space between consecutive company blocks in the experience section.',
  exp_role_indent_em: 'Left indent for role blocks within a company (0.6em). Reduces available width for bullet text wrapping.',
  exp_role_margin_bottom_in: 'Space between consecutive roles within the same company.',
  exp_bullet_padding_left_em: 'Left padding for bullet list (1.4em for disc markers). Reduces available width for bullet text wrapping.',
  exp_bullet_margin_bottom_in: 'Tiny gap between consecutive experience bullets (~1px).',
  exp_company_desc_margin_top_in: 'Space above the italic company description line.',
  exp_company_desc_margin_bottom_in: 'Space below the italic company description, before the first role.',

  // Skills Grid
  skills_columns: 'Number of columns in the skills grid (3). Skills are distributed evenly across columns.',
  skills_bullet_padding_left_em: 'Left padding for skill bullet markers within each column (1.2em).',
  skills_item_margin_bottom_in: 'Tiny gap between skill items within a column (~1px).',

  // Highlights
  highlights_padding_left_em: 'Left padding for highlight bullet markers (1.4em disc indent).',
  highlights_item_margin_bottom_in: 'Space between consecutive highlight bullets.',

  // Minor Sections
  minor_columns: 'Number of columns for minor section grids (3). Education, awards, etc. items flow row-first across columns.',
  minor_item_margin_bottom_in: 'Space between items within a minor section column.',

  // Pruning
  safety_margin_pct: 'Reserve this percentage of page height as error buffer (0.03 = 3%). Prevents estimation errors from causing overflow.',
  experience_prune_bias: 'Score reduction applied to experience bullets during pruning (0.10 = 10%). Makes experience bullets more likely to be removed first.',
  min_bullets_per_role: 'Minimum bullets that must remain per role (2). Pruning will skip a bullet if removing it would go below this threshold.',

  // Bullet Allocation Logic (NOT sizing — these control selection behavior)
  tight_mode_pct: 'Once total whitespace across all pages falls below this fraction (0.10 = 10%), the additive loop switches to "tight mode" and roles can no longer shift between pages to accept new bullets. Lower values (e.g. 0.05) pack more aggressively and lean harder on the subtractive loop to trim overflow; higher values (e.g. 0.15) are more conservative and may under-fill the resume. Sensible range: 0.05 – 0.15.',
  additive_free_pass: 'Number of top-match-score bullets the additive loop will ALWAYS place regardless of tight-mode (5). Guarantees the highest-relevance bullets across all roles land even when the resume is tight. If this forces the resume past target pages, the subtractive loop trims back from the least-relevant end. If you have so many roles that the minimum bullets already overflow, the user must shorten content (cutoff year, unchecking sections) or raise max pages — free-pass will not create space.',
};

interface ConstantGroup {
  label: string;
  tooltip: string;
  keys: string[];
}

const CONSTANT_GROUPS: ConstantGroup[] = [
  {
    label: 'Page Geometry',
    tooltip: 'Physical page dimensions and Chromium rendering scale. These define the virtual CSS viewport used for layout calculation.',
    keys: ['page_width_in', 'page_height_in', 'chromium_scale', 'body_padding_top_in', 'body_padding_bottom_in', 'body_padding_left_in', 'body_padding_right_in', 'body_max_width_in'],
  },
  {
    label: 'Font Sizes (pt)',
    tooltip: 'CSS font sizes in points for each text element type. Used with character width tables to calculate line wrapping.',
    keys: ['font_size_name_pt', 'font_size_base_pt', 'font_size_section_pt', 'font_size_sm_pt', 'font_size_xs_pt', 'font_size_contact_pt'],
  },
  {
    label: 'Line Heights',
    tooltip: 'CSS line-height multipliers. Multiplied by font size to get the vertical space per line of text.',
    keys: ['line_height', 'contact_line_height'],
  },
  {
    label: 'Spacing (inches)',
    tooltip: 'Maps to CSS --spacing-* variables. Controls gaps between elements (xs=0.15rem, sm=0.35rem, md=0.65rem, lg=1rem, xl=1.4rem).',
    keys: ['spacing_xs_in', 'spacing_sm_in', 'spacing_md_in', 'spacing_lg_in', 'spacing_xl_in'],
  },
  {
    label: 'Header',
    tooltip: 'Resume header block: candidate name (75% width) + contact items (25% width), with bottom border and margin.',
    keys: ['header_name_margin_top_em', 'header_padding_bottom_in', 'header_border_in', 'header_margin_bottom_in', 'header_contact_width_pct'],
  },
  {
    label: 'Section Title',
    tooltip: 'The h2 section headings (e.g., "Experience", "Education"). Includes underline border and bottom margin before content.',
    keys: ['section_title_padding_bottom_in', 'section_title_border_in', 'section_title_margin_bottom_in', 'section_margin_bottom_in'],
  },
  {
    label: 'Experience',
    tooltip: 'Company/role/bullet hierarchy spacing. Role indent and bullet padding reduce available width for line-wrapping calculations.',
    keys: ['exp_company_margin_bottom_in', 'exp_role_indent_em', 'exp_role_margin_bottom_in', 'exp_bullet_padding_left_em', 'exp_bullet_margin_bottom_in', 'exp_company_desc_margin_top_in', 'exp_company_desc_margin_bottom_in'],
  },
  {
    label: 'Skills Grid',
    tooltip: 'Skills render in a 3-column grid. Height equals the tallest column. Bullet padding reduces available width per column.',
    keys: ['skills_columns', 'skills_bullet_padding_left_em', 'skills_item_margin_bottom_in'],
  },
  {
    label: 'Highlights',
    tooltip: 'AI-generated highlight bullets. Full-width bulleted list with left padding for disc markers.',
    keys: ['highlights_padding_left_em', 'highlights_item_margin_bottom_in'],
  },
  {
    label: 'Minor Sections',
    tooltip: 'Education, Awards, Presentations, Publications, Portfolios. Rendered in a 3-column grid with break-inside: avoid.',
    keys: ['minor_columns', 'minor_item_margin_bottom_in'],
  },
  {
    label: 'Pruning',
    tooltip: 'Controls automatic content removal behavior. Safety margin reserves extra space for estimation error. Bias makes experience bullets more likely to be pruned first.',
    keys: ['safety_margin_pct', 'experience_prune_bias', 'min_bullets_per_role'],
  },
  {
    label: 'Bullet Allocation Logic',
    tooltip: 'LOGIC knobs — NOT sizing / scale. These tune how the additive + subtractive loops decide which experience bullets land in the PDF. Changing these can shift which bullets are selected for a given user-job, even at identical physical page dimensions.',
    keys: ['tight_mode_pct', 'additive_free_pass'],
  },
];

interface BlockDetail {
  type: string;
  section: string;
  height_in: number;
  total_height_in: number;
  keep_together: boolean;
  prunable: boolean;
  match_score: number;
  item_path: string;
  children?: BlockDetail[];
}

interface SimResult {
  total_pages: number;
  total_content_height_in: number;
  usable_height_per_page_in: number;
  pages: Array<{
    page_num: number;
    used_height_in: number;
    available_height_in: number;
    whitespace_in: number;
    utilization_pct: number;
    block_count: number;
  }>;
  blocks: BlockDetail[];
  sections_found: string[];
}

// Compute page breaks from blocks + usable height
function computePageBreaks(blocks: SimResult['blocks'], usableHeight: number): number[] {
  const breakAfterIndices: number[] = [];
  let remaining = usableHeight;
  let blockIndex = 0;

  for (const block of blocks) {
    const height = block.total_height_in;

    if (height > remaining) {
      if (block.keep_together && height <= usableHeight) {
        // Block moves to next page
        breakAfterIndices.push(blockIndex - 1);
        remaining = usableHeight - height;
      } else if (block.keep_together && height > usableHeight) {
        // Oversized block - may span pages
        if (remaining < usableHeight * 0.1) {
          breakAfterIndices.push(blockIndex - 1);
          remaining = usableHeight;
        }
        let overflow = height - remaining;
        while (overflow > 0) {
          breakAfterIndices.push(blockIndex);
          remaining = usableHeight;
          overflow -= usableHeight;
        }
        remaining = -overflow;
      } else {
        // Non-keep-together overflow
        let overflow = height - remaining;
        while (overflow > 0) {
          breakAfterIndices.push(blockIndex);
          remaining = usableHeight;
          overflow -= usableHeight;
        }
        remaining = -overflow;
      }
    } else {
      remaining -= height;
    }
    blockIndex++;
  }
  return breakAfterIndices;
}

export function PdfCalibration() {
  const [constants, setConstants] = useState<Record<string, number>>({});
  const [defaults, setDefaults] = useState<Record<string, number>>({});
  const [overrides, setOverrides] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  // Simulator state
  const [simUrl, setSimUrl] = useState('https://stephen.skillsnap.me/snowflake/senior-manager-solution-engineering/Resume.html');
  const [simResult, setSimResult] = useState<SimResult | null>(null);
  const [simLoading, setSimLoading] = useState(false);
  const [simError, setSimError] = useState<string | null>(null);

  // Collapsible groups
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['Page Geometry', 'Pruning', 'Bullet Allocation Logic']));
  const [expandedBlocks, setExpandedBlocks] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getPdfCalibration();
      setConstants(result.constants);
      setDefaults(result.defaults);
      setOverrides(result.overrides);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to load calibration constants');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleConstantChange = (key: string, value: string) => {
    const num = parseFloat(value);
    if (isNaN(num)) return;
    setConstants(prev => ({ ...prev, [key]: num }));
    if (num !== defaults[key]) {
      setOverrides(prev => ({ ...prev, [key]: num }));
    } else {
      setOverrides(prev => { const next = { ...prev }; delete next[key]; return next; });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage(null);
    try {
      await api.updatePdfCalibration(overrides);
      setSaveMessage(`Saved ${Object.keys(overrides).length} override(s)`);
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleResetAll = () => { setConstants({ ...defaults }); setOverrides({}); };

  const handleResetOne = (key: string) => {
    setConstants(prev => ({ ...prev, [key]: defaults[key] }));
    setOverrides(prev => { const next = { ...prev }; delete next[key]; return next; });
  };

  const handleSimulate = async () => {
    setSimLoading(true);
    setSimError(null);
    try {
      const result = await api.simulatePdfLayout({
        url: simUrl,
        overrides: Object.keys(overrides).length > 0 ? overrides : undefined,
      });
      setSimResult(result as unknown as SimResult);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setSimError(axiosErr?.response?.data?.message || 'Simulation failed');
    } finally {
      setSimLoading(false);
    }
  };

  const toggleGroup = (label: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label); else next.add(label);
      return next;
    });
  };

  const isOverridden = (key: string) => key in overrides;

  // Compute page break positions for block details
  const pageBreaks = simResult
    ? new Set(computePageBreaks(simResult.blocks, simResult.usable_height_per_page_in))
    : new Set<number>();

  // Recursive block row renderer
  const renderBlockRow = (block: BlockDetail, depth: number): React.ReactNode => {
    const indent = depth * 16; // px per nesting level
    const isContainer = (block.children?.length ?? 0) > 0;
    const textColor = depth === 0 ? 'text-gray-700' : depth === 1 ? 'text-gray-500' : 'text-gray-400';
    const borderStyle = depth === 0 ? 'border-b border-gray-100' : 'border-b border-gray-50';

    return (
      <div key={`${block.type}-${block.item_path}-${depth}`}>
        <div className={`grid grid-cols-[3fr_2fr_1.5fr_1.5fr_1fr] gap-x-2 ${borderStyle} hover:bg-gray-50 ${textColor}`}>
          <span className="py-0.5" style={{ paddingLeft: `${indent}px` }}>
            {isContainer ? '▸ ' : ''}{block.type}
          </span>
          <span className="py-0.5">{depth === 0 ? block.section : ''}</span>
          <span className="py-0.5 text-right">{block.height_in.toFixed(4)}</span>
          <span className={`py-0.5 text-right ${depth === 0 ? 'font-medium' : ''}`}>{block.total_height_in.toFixed(4)}</span>
          <span className="py-0.5 text-center">
            {block.keep_together ? '🔒' : ''}{block.prunable ? '✂️' : ''}
          </span>
        </div>
        {block.children?.map((child, j) => (
          <div key={j}>{renderBlockRow(child, depth + 1)}</div>
        ))}
      </div>
    );
  };

  if (loading && Object.keys(constants).length === 0) {
    return (
      <div className="p-6 flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">PDF Calibration</h1>
          <p className="text-sm text-gray-500 mt-1">
            Tune vertical space estimation constants for deterministic page fitting
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleResetAll} className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-1">
            <RotateCcw className="w-4 h-4" /> Reset All
          </button>
          <button onClick={handleSave} disabled={saving || Object.keys(overrides).length === 0}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-1">
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : `Save (${Object.keys(overrides).length})`}
          </button>
          <button onClick={loadData} disabled={loading} className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-1">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {error}</div>}
      {saveMessage && <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-lg">{saveMessage}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Constants Editor */}
        <div className="space-y-3">
          <Tip text="Calibration constants that control the vertical space estimator. Modify values, then Simulate to see the effect. Save to persist overrides to DynamoDB."><h2 className="text-lg font-semibold text-gray-700 cursor-help">Constants</h2></Tip>
          {CONSTANT_GROUPS.map(group => (
            <div key={group.label} className="bg-white rounded-lg shadow">
              <button
                onClick={() => toggleGroup(group.label)}
                className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 rounded-t-lg"
              >
                <Tip text={group.tooltip}><span className="font-medium text-gray-700 cursor-help">{group.label}</span></Tip>
                <div className="flex items-center gap-2">
                  {group.keys.some(k => isOverridden(k)) && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">modified</span>
                  )}
                  {expandedGroups.has(group.label) ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                </div>
              </button>
              {expandedGroups.has(group.label) && (
                <div className="px-4 pb-3 space-y-2">
                  {group.keys.map(key => (
                    <div key={key} className={`flex items-center justify-between gap-2 p-2 rounded ${isOverridden(key) ? 'bg-amber-50' : 'bg-gray-50'}`}>
                      <Tip text={CONSTANT_TIPS[key] || key}>
                        <label className="text-xs font-mono text-gray-600 truncate cursor-help">
                          {key}
                        </label>
                      </Tip>
                      <div className="flex items-center gap-1 ml-auto">
                        <input
                          type="number" step="any"
                          value={constants[key] ?? ''}
                          onChange={(e) => handleConstantChange(key, e.target.value)}
                          className={`w-24 px-2 py-1 text-sm border rounded text-right font-mono ${isOverridden(key) ? 'border-amber-300 bg-amber-50' : 'border-gray-300'}`}
                        />
                        {isOverridden(key) ? (
                          <button onClick={() => handleResetOne(key)} className="text-gray-400 hover:text-gray-600 w-4" title={`Reset to default (${defaults[key]})`}>
                            <RotateCcw className="w-3 h-3" />
                          </button>
                        ) : <span className="w-4" />}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Right: Simulator */}
        <div className="space-y-4">
          <Tip text="Fetch a live Resume.html URL and run the page estimator against it. Compare predicted pages vs actual PDF to calibrate constants."><h2 className="text-lg font-semibold text-gray-700 cursor-help">Simulator</h2></Tip>

          {/* URL Input */}
          <div className="bg-white rounded-lg shadow p-4">
            <Tip text="URL of a generated Resume.html file. The HTML is fetched and parsed to extract section data for measurement."><label className="block text-sm font-medium text-gray-600 mb-2 cursor-help">Resume HTML URL</label></Tip>
            <div className="flex gap-2">
              <input type="text" value={simUrl} onChange={(e) => setSimUrl(e.target.value)}
                placeholder="https://username.skillsnap.me/.../Resume.html"
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg font-mono" />
              <button onClick={handleSimulate} disabled={simLoading || !simUrl}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1">
                {simLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                Simulate
              </button>
            </div>
          </div>

          {simError && <div className="p-3 bg-red-50 text-red-700 rounded-lg flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {simError}</div>}

          {/* Simulation Results */}
          {simResult && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-3">
                <Tip text="Estimated total pages after simulating pagination with keep-together rules">
                  <div className="bg-white rounded-lg shadow p-4 text-center w-full cursor-help">
                    <div className="text-3xl font-bold text-primary-600">{simResult.total_pages}</div>
                    <div className="text-xs text-gray-500">Pages</div>
                  </div>
                </Tip>
                <Tip text="Sum of all content block heights in CSS inches (before page-break whitespace)">
                  <div className="bg-white rounded-lg shadow p-4 text-center w-full cursor-help">
                    <div className="text-3xl font-bold text-gray-700">{simResult.total_content_height_in.toFixed(2)}"</div>
                    <div className="text-xs text-gray-500">Total Content</div>
                  </div>
                </Tip>
                <Tip text="Available vertical space per page after margins and safety buffer (CSS inches in virtual viewport)">
                  <div className="bg-white rounded-lg shadow p-4 text-center w-full cursor-help">
                    <div className="text-3xl font-bold text-gray-700">{simResult.usable_height_per_page_in.toFixed(2)}"</div>
                    <div className="text-xs text-gray-500">Usable/Page</div>
                  </div>
                </Tip>
              </div>

              {/* Page Breakdown */}
              <div className="bg-white rounded-lg shadow p-4">
                <Tip text="Per-page utilization showing content vs whitespace. Low utilization means keep-together blocks pushed content to the next page."><h3 className="font-medium text-gray-700 mb-3 cursor-help">Page Breakdown</h3></Tip>
                <div className="space-y-2">
                  {simResult.pages.map(page => (
                    <div key={page.page_num} className="flex items-center gap-3">
                      <span className="text-sm font-mono w-16 text-gray-500">Page {page.page_num}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden relative">
                        <div
                          className={`h-full rounded-full ${page.utilization_pct > 90 ? 'bg-green-500' : page.utilization_pct > 70 ? 'bg-blue-500' : 'bg-amber-500'}`}
                          style={{ width: `${Math.min(page.utilization_pct, 100)}%` }}
                        />
                        <span className="absolute inset-0 flex items-center justify-center text-xs font-medium">
                          {page.utilization_pct}% ({page.used_height_in.toFixed(2)}" / {page.available_height_in.toFixed(2)}")
                        </span>
                      </div>
                      <span className="text-xs text-gray-400 w-20 text-right">{page.whitespace_in.toFixed(2)}" free</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Section Heights */}
              <div className="bg-white rounded-lg shadow p-4">
                <Tip text="Total vertical space consumed by each resume section. Sections not present in this resume won't appear (e.g., publications/portfolios if the resume doesn't have them)."><h3 className="font-medium text-gray-700 mb-3 cursor-help">Section Heights</h3></Tip>
                <div className="space-y-1">
                  {(() => {
                    const sectionTotals: Record<string, number> = {};
                    simResult.blocks.forEach(b => {
                      const sec = b.section || 'other';
                      sectionTotals[sec] = (sectionTotals[sec] || 0) + b.total_height_in;
                    });
                    const totalH = Object.values(sectionTotals).reduce((a, b) => a + b, 0);
                    return Object.entries(sectionTotals)
                      .sort((a, b) => b[1] - a[1])
                      .map(([sec, h]) => (
                        <div key={sec} className="flex items-center gap-2">
                          <span className="text-sm w-28 text-gray-600 capitalize">{sec}</span>
                          <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                            <div className="h-full bg-primary-400 rounded-full" style={{ width: `${(h / totalH) * 100}%` }} />
                          </div>
                          <span className="text-xs font-mono text-gray-500 w-16 text-right">{h.toFixed(3)}"</span>
                        </div>
                      ));
                  })()}
                </div>
              </div>

              {/* Block Details (collapsible) */}
              <div className="bg-white rounded-lg shadow">
                <button onClick={() => setExpandedBlocks(!expandedBlocks)}
                  className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50">
                  <Tip text="Every measured content block in document order. Parent blocks (height=0) are keep-together containers whose Total includes all children. Red divider lines show estimated page breaks.">
                    <span className="font-medium text-gray-700 cursor-help">Block Details ({simResult.blocks.length} blocks)</span>
                  </Tip>
                  {expandedBlocks ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                {expandedBlocks && (
                  <div className="px-4 pb-4 max-h-[600px] overflow-auto text-xs font-mono">
                    {/* Header row */}
                    <div className="grid grid-cols-[3fr_2fr_1.5fr_1.5fr_1fr] gap-x-2 text-gray-500 border-b py-1.5 sticky top-0 bg-white z-10">
                      <Tip text="Block type identifier: header, section_title, summary_text, highlight_bullet, skills_grid, exp_company_lead, exp_role, exp_bullet, minor_grid, section_spacing, etc."><span className="cursor-help">Type</span></Tip>
                      <Tip text="Which resume section this block belongs to (contact, summary, highlights, skills, experience, education, awards, presentations, publications, portfolios)"><span className="cursor-help">Section</span></Tip>
                      <Tip text="This block's own height in inches, excluding any nested children. Container blocks like exp_company_lead have height=0 since they only hold children."><span className="text-right cursor-help">Height</span></Tip>
                      <Tip text="Total height including this block plus all nested children. For leaf blocks, equals Height. For containers (keep-together groups), equals sum of all children's heights."><span className="text-right cursor-help">Total</span></Tip>
                      <Tip text="🔒 = Keep-Together (block moves as a unit, won't split across pages). ✂️ = Prunable (can be removed by the page-fit algorithm to reduce page count)."><span className="text-center cursor-help">KT / Prune</span></Tip>
                    </div>
                    {/* Block rows */}
                    {simResult.blocks.map((block, i) => {
                      const showBreak = pageBreaks.has(i);
                      return (
                        <div key={`b-${i}`}>
                          {renderBlockRow(block, 0)}
                          {showBreak && (
                            <div className="flex items-center gap-2 py-1.5 px-2 bg-red-50 border-y-2 border-red-300 border-dashed">
                              <span className="text-red-500 font-bold text-[10px] tracking-wider uppercase">Page Break</span>
                              <div className="flex-1 border-t border-red-300 border-dashed" />
                              <span className="text-red-400 text-[10px]">Page {Array.from(pageBreaks).filter(b => b <= i).length + 1} starts below</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
